import { omit } from "@mixitone/util";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

const SELECT_CHEVRON_STYLE: React.CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 8 8'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.2' d='M2 3l2 2 2-2'/%3e%3c/svg%3e\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.5rem center",
  backgroundSize: "0.8em 0.8em",
};

interface Props extends React.SelectHTMLAttributes<HTMLSelectElement> {
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Tighter padding for inline/table use (matches compact Button / GroupSelect). */
  compact?: boolean;
}

export const Select: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [hasChanged, setHasChanged] = useState(false);
  const [value, setValue] = useState(props.value);
  const rest = omit(props, ["value", "onChange", "children", "compact"]);
  const { children, compact } = props;

  useEffect(() => {
    if (hasChanged) return;
    setValue(props.value);
  }, [props.value, hasChanged]);

  useEffect(() => {
    if (!hasChanged) return;

    props.onChange({
      target: { value },
    } as React.ChangeEvent<HTMLSelectElement>);
  }, [hasChanged, value]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHasChanged(true);
    setValue(e.target.value);
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      {...rest}
      style={{ ...SELECT_CHEVRON_STYLE, ...rest.style }}
      className={twMerge(
        "mixitone-select appearance-none rounded border border-gray-300 bg-white stroke-gray-500 text-left dark:border-gray-600",
        compact
          ? "inline-block w-auto min-h-0 px-2 py-1 pr-7 text-xs leading-4"
          : "mt-1 block w-full min-h-[42px] p-2 pr-7",
        rest.className,
      )}
    >
      {children}
    </select>
  );
};
